import { useState, useMemo, useCallback } from 'react';

import { Select, Button, toast } from '@pella/sash-framework';

import {
  useDivisions,
  useDivisionLocations,
  useCostCenters,
  useAccounts,
  useSegments,
  useIntercomps,
} from '../../../hooks/useInvManagement';
import styles from './ChartOfAccountSelector.module.css';

// istanbul ignore next -- all fields define render; displayField fallback is defensive
const renderOptionLabel = (f, v) => (f.render ? f.render(v) : v[f.displayField]);

/**
 * Chart Of Account Selector component that allows users to select a hierarchical
 * chart of accounts by choosing division, location, cost center, account, segment,
 * and inter-company values.
 * @param {object} props - Component props
 * @param {string} [props.defaultDestinationAcct] - Default account value to pre-fill
 * @param {(selectedValue: object) => void} props.selectionOnClick - Callback when selection is confirmed
 * @param {() => void} props.cancelOnClick - Callback when cancel is clicked
 * @returns {import("react").ReactElement} Chart of account selector component
 */
const ChartOfAccountSelector = (props) => {
  const [selectedFullValue, setSelectedFullValue] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const defaultDestinationAcct = props.defaultDestinationAcct;
  let defaultAccount = {};

  if (defaultDestinationAcct) {
    defaultAccount = {
      division: defaultDestinationAcct.slice(0, 3),
      location: defaultDestinationAcct.slice(4, 7),
      costCenter: defaultDestinationAcct.slice(8, 14),
    };
  }

  const { divisions } = useDivisions();
  const { divisionLocations } = useDivisionLocations(selectedFullValue.division?.value);
  const { costCenters } = useCostCenters(selectedFullValue.division?.value, selectedFullValue.location?.value);
  const { accounts } = useAccounts(
    selectedFullValue.division?.value,
    selectedFullValue.location?.value,
    selectedFullValue.costCenter?.value,
  );
  const { segments } = useSegments(
    selectedFullValue.division?.value,
    selectedFullValue.location?.value,
    selectedFullValue.costCenter?.value,
    selectedFullValue.account?.value,
  );
  const { intercomps } = useIntercomps(
    selectedFullValue.division?.value,
    selectedFullValue.location?.value,
    selectedFullValue.costCenter?.value,
    selectedFullValue.account?.value,
    selectedFullValue.segment?.value,
  );

  // Direct map from field name to query result — replaces inputData state + 6 sync effects
  const dataMap = useMemo(
    () => ({
      division: divisions,
      location: divisionLocations,
      costCenter: costCenters,
      account: accounts,
      segment: segments,
      interCompany: intercomps,
    }),
    [divisions, divisionLocations, costCenters, accounts, segments, intercomps],
  );

  const fields = useMemo(
    () => [
      {
        name: 'Select Division',
        static: true,
        displayField: 'description',
        render: (option) => `(${option.division}) ${option.description}`,
        defaultValue: defaultAccount.division,
        valueField: 'division',
      },
      {
        name: 'Select Location',
        static: false,
        displayField: 'description',
        render: (option) => `(${option.location}) ${option.description}`,
        defaultValue: defaultAccount.location,
        valueField: 'location',
      },
      {
        name: 'Select Cost Center',
        static: false,
        displayField: 'description',
        render: (option) => `(${option.costCenter}) ${option.description}`,
        defaultValue: defaultAccount.costCenter,
        valueField: 'costCenter',
      },
      {
        name: 'Select Account',
        static: false,
        displayField: 'description',
        render: (option) => `(${option.account}) ${option.description}`,
        valueField: 'account',
      },
      {
        name: 'Select Segment',
        static: false,
        displayField: 'description',
        render: (option) => `(${option.segment}) ${option.description}`,
        valueField: 'segment',
      },
      {
        name: 'Select Inter Company',
        static: false,
        displayField: 'description',
        render: (option) => `(${option.interCompany}) ${option.description}`,
        valueField: 'interCompany',
      },
    ],
    [defaultAccount.division, defaultAccount.location, defaultAccount.costCenter],
  );

  const setFullValue = useCallback(
    ({ field, label, value }, index, fullField) => {
      const downstreamFields = fields.slice(index + 1).map((f) => f.valueField);

      setSelectedFullValue((prev) => {
        if (prev[field]?.value === value) return prev;
        const next = { ...prev };
        for (const f of downstreamFields) delete next[f];
        next[field] = { label, value, raw: fullField };
        return next;
      });

      setActiveIndex(index + 1);
    },
    [fields],
  );

  // Auto-select pre-fill values when query data loads (render-time adjustment, one field per render pass)
  for (const [index, f] of fields.entries()) {
    if (f.defaultValue && dataMap[f.valueField] && !selectedFullValue[f.valueField]) {
      const match = dataMap[f.valueField].find((v) => v[f.valueField] === f.defaultValue);
      if (match) {
        setFullValue({ field: f.valueField, value: match[f.valueField], label: match[f.displayField] }, index, match);
        break;
      }
    }
  }

  const validateSelection = () =>
    Boolean(
      selectedFullValue.division &&
      selectedFullValue.location &&
      selectedFullValue.costCenter &&
      selectedFullValue.account &&
      selectedFullValue.segment &&
      selectedFullValue.interCompany,
    );

  const selectOnClick = () => {
    const isValid = validateSelection();
    if (isValid) {
      const selectedValue = `${selectedFullValue.division.value}.${selectedFullValue.location.value}.${selectedFullValue.costCenter.value}.${selectedFullValue.account.value}.${selectedFullValue.segment.value}.${selectedFullValue.interCompany.value}`;
      props.selectionOnClick(selectedValue);
    } else {
      toast.warning('Current Selection is invalid!');
    }
  };

  const cancelOnClick = () => {
    props.cancelOnClick();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h3>Chart Of Account Selector</h3>
        </div>
      </div>
      <div className={styles.fieldsContainer}>
        {fields.map((f, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`field_${f.valueField}_${index}`} className={styles.fieldWrapper}>
            <Select
              id={`id_${f.name}`}
              labelText={f.name}
              value={selectedFullValue[f.valueField]?.value || ''}
              disabled={index > activeIndex || !dataMap[f.valueField]}
              onChange={(e) =>
                setFullValue(
                  {
                    field: f.valueField,
                    value: e.target.value,
                    label: dataMap[f.valueField]?.find((v) => v[f.valueField] === e.target.value)?.[f.displayField],
                  },
                  index,
                  dataMap[f.valueField]?.find((v) => v[f.valueField] === e.target.value),
                )
              }
            >
              <option value=''>Choose...</option>
              {dataMap[f.valueField]?.map((v) => (
                <option key={`${f.valueField}_${v[f.valueField]}`} value={v[f.valueField]}>
                  {renderOptionLabel(f, v)}
                </option>
              ))}
            </Select>
          </div>
        ))}
      </div>
      <div className={styles.buttonContainer}>
        <Button color='secondary' size='sm' onClick={cancelOnClick}>
          Cancel
        </Button>
        <Button color='primary' size='sm' onClick={selectOnClick}>
          Set
        </Button>
      </div>
    </div>
  );
};

export default ChartOfAccountSelector;
